import { newToast } from '../components';
import { 
  SysI_MicrobiologiaMicroorganismosCatalogo, 
  SysI_MicrobiologiaAntibioticosCatalogo,
  SysI_MicrobiologiaConfiguracion 
} from '../types/types.microbiology';
import useApi from '../utils/axios-pettitions';

export const useApiMicrobiology = () => {
  const { runApi } = useApi();

  // ====================================================================
  // ENDPOINTS GENERALES
  // ====================================================================

  /**
   * Obtiene todos los catálogos (microorganismos y antibióticos)
   * GET: api/Microbiology
   */
  const getAllCatalogs = async () => {
    return runApi('Microbiology', 'GET', null, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return null;
      } else if (res.internalCode === 101) {
        return res.data as {
          Microorganismos: SysI_MicrobiologiaMicroorganismosCatalogo[];
          Antibioticos: SysI_MicrobiologiaAntibioticosCatalogo[];
        };
      }
      return null;
    });
  };

  // ====================================================================
  // MICROORGANISMOS
  // ====================================================================

  /**
   * Obtiene todos los microorganismos activos
   * GET: api/Microbiology/getMicroorganismos
   */
  const getMicroorganismos = async () => {
    return runApi('Microbiology/getMicroorganismos', 'GET', null, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return [];
      } else if (res.internalCode === 101) {
        return res.data as SysI_MicrobiologiaMicroorganismosCatalogo[];
      } else if (res.internalCode === 104) {
        return [];
      }
      return [];
    });
  };

  /**
   * Obtiene un microorganismo por ID
   * GET: api/Microbiology/getMicroorganismoById?id=5
   */
  const getMicroorganismoById = async (id: number) => {
    return runApi(`Microbiology/getMicroorganismoById?id=${id}`, 'GET', null, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return null;
      } else if (res.internalCode === 101) {
        return res.data as SysI_MicrobiologiaMicroorganismosCatalogo;
      } else if (res.internalCode === 104) {
        return null;
      }
      return null;
    });
  };

  /**
   * Crea un nuevo microorganismo
   * POST: api/Microbiology/createMicroorganismo
   */
  const createMicroorganismo = async (microorganismo: SysI_MicrobiologiaMicroorganismosCatalogo) => {
    return runApi('Microbiology/createMicroorganismo', 'POST', null, microorganismo).then((res) => {
      if (res.internalCode === 105) {
        newToast('SUCCESS', 'Microorganismo creado correctamente', 5);
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al crear microorganismo');
      }
    });
  };

  /**
   * Actualiza un microorganismo existente
   * POST: api/Microbiology/updateMicroorganismo
   */
  const updateMicroorganismo = async (microorganismo: SysI_MicrobiologiaMicroorganismosCatalogo) => {
    return runApi('Microbiology/updateMicroorganismo', 'POST', null, microorganismo).then((res) => {
      if (res.internalCode === 107) {
        newToast('SUCCESS', 'Microorganismo actualizado correctamente', 5);
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al actualizar microorganismo');
      }
    });
  };

  /**
   * Desactiva un microorganismo (eliminación lógica)
   * POST: api/Microbiology/deleteMicroorganismo
   */
  const deleteMicroorganismo = async (id: number) => {
    return runApi('Microbiology/deleteMicroorganismo', 'POST', null, { Id: id }).then((res) => {
      if (res.internalCode === 108) {
        newToast('SUCCESS', 'Microorganismo desactivado correctamente', 5);
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al desactivar microorganismo');
      }
    });
  };

  /**
   * Valida si un microorganismo puede ser eliminado
   * GET: api/Microbiology/validarEliminacionMicroorganismo?id=5
   */
  const validarEliminacionMicroorganismo = async (id: number) => {
    return runApi(`Microbiology/validarEliminacionMicroorganismo?id=${id}`, 'GET', null, null).then((res) => {
      if (res.internalCode === 101) {
        return res.data as { CanDelete: boolean; Message: string };
      } else {
        newToast('ERROR', res.message, 3);
        return null;
      }
    });
  };

  // ====================================================================
  // ANTIBIÓTICOS
  // ====================================================================

  /**
   * Obtiene todos los antibióticos activos
   * GET: api/Microbiology/getAntibioticos
   */
  const getAntibioticos = async () => {
    return runApi('Microbiology/getAntibioticos', 'GET', null, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return [];
      } else if (res.internalCode === 101) {
        return res.data as SysI_MicrobiologiaAntibioticosCatalogo[];
      } else if (res.internalCode === 104) {
        return [];
      }
      return [];
    });
  };

  /**
   * Obtiene un antibiótico por ID
   * GET: api/Microbiology/getAntibioticoById?id=5
   */
  const getAntibioticoById = async (id: number) => {
    return runApi(`Microbiology/getAntibioticoById?id=${id}`, 'GET', null, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return null;
      } else if (res.internalCode === 101) {
        return res.data as SysI_MicrobiologiaAntibioticosCatalogo;
      } else if (res.internalCode === 104) {
        return null;
      }
      return null;
    });
  };

  /**
   * Crea un nuevo antibiótico
   * POST: api/Microbiology/createAntibiotico
   */
  const createAntibiotico = async (antibiotico: SysI_MicrobiologiaAntibioticosCatalogo) => {
    return runApi('Microbiology/createAntibiotico', 'POST', null, antibiotico).then((res) => {
      if (res.internalCode === 105) {
        newToast('SUCCESS', 'Antibiótico creado correctamente', 5);
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al crear antibiótico');
      }
    });
  };

  /**
   * Actualiza un antibiótico existente
   * POST: api/Microbiology/updateAntibiotico
   */
  const updateAntibiotico = async (antibiotico: SysI_MicrobiologiaAntibioticosCatalogo) => {
    return runApi('Microbiology/updateAntibiotico', 'POST', null, antibiotico).then((res) => {
      if (res.internalCode === 107) {
        newToast('SUCCESS', 'Antibiótico actualizado correctamente', 5);
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al actualizar antibiótico');
      }
    });
  };

  /**
   * Desactiva un antibiótico (eliminación lógica)
   * POST: api/Microbiology/deleteAntibiotico
   */
  const deleteAntibiotico = async (id: number) => {
    return runApi('Microbiology/deleteAntibiotico', 'POST', null, { Id: id }).then((res) => {
      if (res.internalCode === 108) {
        newToast('SUCCESS', 'Antibiótico desactivado correctamente', 5);
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al desactivar antibiótico');
      }
    });
  };

  /**
   * Valida si un antibiótico puede ser eliminado
   * GET: api/Microbiology/validarEliminacionAntibiotico?id=5
   */
  const validarEliminacionAntibiotico = async (id: number) => {
    return runApi(`Microbiology/validarEliminacionAntibiotico?id=${id}`, 'GET', null, null).then((res) => {
      if (res.internalCode === 101) {
        return res.data as { CanDelete: boolean; Message: string };
      } else {
        newToast('ERROR', res.message, 3);
        return null;
      }
    });
  };

  // ====================================================================
  // CONFIGURACIÓN DE ANALITOS
  // ====================================================================

  /**
   * Obtiene la configuración de microbiología de un analito
   * GET: api/Microbiology/getConfiguracionAnalito?analitoId=150
   */
  const getConfiguracionAnalito = async (analitoId: number) => {
    return runApi(`Microbiology/getConfiguracionAnalito?analitoId=${analitoId}`, 'GET', null, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return null;
      } else if (res.internalCode === 101) {
        return res.data as SysI_MicrobiologiaConfiguracion;
      }
      return null;
    });
  };

  /**
   * Obtiene todos los analitos con configuración de microbiología
   * GET: api/Microbiology/getAnalitosConConfiguracion
   */
  const getAnalitosConConfiguracion = async () => {
    return runApi('Microbiology/getAnalitosConConfiguracion', 'GET', null, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return [];
      } else if (res.internalCode === 101) {
        return res.data as SysI_MicrobiologiaConfiguracion[];
      } else if (res.internalCode === 104) {
        return [];
      }
      return [];
    });
  };

  /**
   * Guarda o actualiza la configuración de un analito
   * POST: api/Microbiology/saveConfiguracionAnalito
   */
  const saveConfiguracionAnalito = async (config: SysI_MicrobiologiaConfiguracion) => {
    return runApi('Microbiology/saveConfiguracionAnalito', 'POST', null, config).then((res) => {
      if (res.internalCode === 107) {
        newToast('SUCCESS', 'Configuración guardada correctamente', 5);
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al guardar configuración');
      }
    });
  };

  /**
   * Elimina la configuración de un analito
   * POST: api/Microbiology/deleteConfiguracionAnalito
   */
  const deleteConfiguracionAnalito = async (analitoId: number) => {
    return runApi('Microbiology/deleteConfiguracionAnalito', 'POST', null, { Id: analitoId }).then((res) => {
      if (res.internalCode === 108) {
        newToast('SUCCESS', 'Configuración eliminada correctamente', 5);
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al eliminar configuración');
      }
    });
  };

  /**
   * Obtiene el panel de antibióticos sugerido para un microorganismo en un analito específico
   * GET: api/Microbiology/getPanelAntibioticosParaMicroorganismo?analitoId=150&microorganismoId=22
   */
  const getPanelAntibioticosParaMicroorganismo = async (analitoId: number, microorganismoId: number) => {
    return runApi(`Microbiology/getPanelAntibioticosParaMicroorganismo?analitoId=${analitoId}&microorganismoId=${microorganismoId}`, 'GET', null, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return [];
      } else if (res.internalCode === 101) {
        return res.data as number[];
      } else if (res.internalCode === 104) {
        return [];
      }
      return [];
    });
  };

  // ====================================================================
  // RETORNAR TODAS LAS FUNCIONES
  // ====================================================================

  return {
    // General
    getAllCatalogs,
    
    // Microorganismos
    getMicroorganismos,
    getMicroorganismoById,
    createMicroorganismo,
    updateMicroorganismo,
    deleteMicroorganismo,
    validarEliminacionMicroorganismo,
    
    // Antibióticos
    getAntibioticos,
    getAntibioticoById,
    createAntibiotico,
    updateAntibiotico,
    deleteAntibiotico,
    validarEliminacionAntibiotico,
    
    // Configuración
    getConfiguracionAnalito,
    getAnalitosConConfiguracion,
    saveConfiguracionAnalito,
    deleteConfiguracionAnalito,
    getPanelAntibioticosParaMicroorganismo,
  };
};